import React, { useState, useEffect, useRef } from "react";
import { useBranding } from "../context/BrandingContext";

export default function WhatsAppWidget() {
  const { branding } = useBranding();
  const whatsappLogo = branding.extras.whatsappLogo || branding.logos.icon;
  const [open, setOpen] = useState(false);
  const [typingPhase, setTypingPhase] = useState('initial'); // 'initial' | 'question' | null
  const [greetingVisible, setGreetingVisible] = useState(false);
  const [questionVisible, setQuestionVisible] = useState(false);
  const typingTimeoutRef = useRef(null);
  const [messageStep, setMessageStep] = useState(1);
  const [userMessage, setUserMessage] = useState('');
  const [userName, setUserName] = useState('');

  // ⚠️ CONFIGURA TU NÚMERO Y MENSAJE AQUÍ
  const WA_PHONE = "50689422525"; // sin + ni espacios
  const WA_DEFAULT_MESSAGE = "Hola, vengo de la web y necesito ayuda 😊";

  // hora estilo "2:09"
  const [timeStr, setTimeStr] = useState("");

  useEffect(() => {
    const now = new Date();
    let h = now.getHours();
    let m = now.getMinutes();
    if (m < 10) m = "0" + m;
    setTimeStr(`${h}:${m}`);
  }, []);

  // cada vez que se abre el popup => corre animación (puntitos -> mensaje)
  useEffect(() => {
    if (open) {
      // estado inicial
      setTypingPhase('initial');
      setGreetingVisible(false);
      setQuestionVisible(false);
      setMessageStep(1);
      setUserMessage('');
      setUserName('');
      if (inputRef.current) inputRef.current.value = '';

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        setTypingPhase(null);
        setGreetingVisible(true);
      }, 1400);
    } else {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }

    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [open]);

  // envío a WhatsApp
  const inputRef = useRef(null);
  const handleSend = () => {
    const rawValue = (inputRef.current && inputRef.current.value || "").trim();

    if (messageStep === 1) {
      const msg = rawValue || WA_DEFAULT_MESSAGE;
      setUserMessage(msg);
      setMessageStep(2);
      setTypingPhase('question');
      setQuestionVisible(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setTypingPhase(null);
        setQuestionVisible(true);
      }, 1200);
      if (inputRef.current) inputRef.current.value = "";
      setTimeout(() => inputRef.current?.focus(), 0);
      return;
    }

    const nameValue = rawValue;
    if (!nameValue) {
      inputRef.current?.focus();
      return;
    }

    setUserName(nameValue);
    const finalMsg = `Hola, soy ${nameValue}. ${userMessage || WA_DEFAULT_MESSAGE}`;
    const encoded = encodeURIComponent(finalMsg);
    const link = `https://wa.me/${WA_PHONE}?text=${encoded}`;
    window.open(link, "_blank");

    if (inputRef.current) inputRef.current.value = "";
    setUserMessage('');
    setUserName('');
    setMessageStep(1);
    setQuestionVisible(false);
    setTypingPhase('initial');
    setOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* estilos aislados del widget */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          :root {
            --wa-green: #25d366;
            --wa-green-dark: #1ebe5d;
            --bubble-bg-user: #ffffff;
            --bubble-typing-bg: #f5f5f5;
            --shadow-xl: 0 24px 48px rgba(0,0,0,.16);
            --radius-lg: 16px;
            --radius-full: 999px;
          }

          #wa-widget-container {
            position: fixed;
            right: 16px;
            bottom: 16px;
            z-index: 999999;
            font-family: system-ui, -apple-system, BlinkMacSystemFont, "Inter", Roboto, "Helvetica Neue", Arial, sans-serif;
          }

          .wa-button {
            width: 64px;
            height: 64px;
            border-radius: var(--radius-full);
            background: var(--wa-green);
            box-shadow: 0 20px 32px rgba(0,0,0,.22);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: all .15s ease-in-out;
          }
          .wa-button:hover {
            background: var(--wa-green-dark);
            box-shadow: 0 28px 40px rgba(0,0,0,.25);
            transform: scale(1.05);
          }

          .wa-icon {
            width: 32px;
            height: 32px;
            color: #fff;
          }

          .wa-chat-window {
            position: absolute;
            right: 0;
            bottom: 80px;
            width: 360px;
            max-width: calc(100vw - 32px);
            background: #fff;
            border-radius: var(--radius-lg);
            box-shadow: var(--shadow-xl);
            overflow: hidden;
            border: 1px solid rgba(0,0,0,.06);
            display: flex;
            flex-direction: column;
          }

          .wa-chat-window.hidden {
            display: none;
          }

          .wa-header {
            position: relative;
            display: flex;
            align-items: flex-start;
            padding: 16px;
            border-bottom: 1px solid rgba(0,0,0,.07);
            background-color: #fff;
          }

          .wa-close-absolute {
            position: absolute;
            top: 16px;
            right: 16px;
            background: transparent;
            border: 0;
            font-size: 18px;
            line-height: 1;
            color: #555;
            cursor: pointer;
            padding: 4px;
          }
          .wa-close-absolute:hover {
            color: #000;
          }

          .wa-header-left {
            display: flex;
            flex-grow: 1;
            min-width: 0;
            padding-right: 32px;
          }

          .wa-avatar-wrapper {
            position: relative;
            margin-right: 12px;
            flex-shrink: 0;
          }

          .wa-avatar {
            width: 52px;
            height: 52px;
            border-radius: 999px;
            background: var(--brand);
            box-shadow: 0 8px 16px rgba(0,0,0,.18);
            overflow: hidden;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .wa-avatar img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .wa-online-dot {
            position: absolute;
            right: 4px;
            bottom: 4px;
            width: 12px;
            height: 12px;
            background: #25d366;
            border-radius: var(--radius-full);
            border: 2px solid #fff;
            box-shadow: 0 0 6px rgba(37,211,102,.6);
          }

          .wa-header-texts {
            min-width: 0;
            display: flex;
            flex-direction: column;
          }

          .wa-brand-name {
            font-size: 16px;
            font-weight: 600;
            color: #111;
            line-height: 1.2;
          }

          .wa-status-line {
            font-size: 14px;
            color: #111;
            line-height: 1.3;
            font-weight: 400;
            display: flex;
            flex-direction: column;
          }

          .wa-online-label {
            color: #000;
            font-weight: 400;
          }

          .wa-messages-area {
            position: relative;
            background-color: #efeae2;
            background-image: url("https://jszgeoosgpozjlvohrjy.supabase.co/storage/v1/object/public/assets/fondos/whatsfondo.jpg");
            background-size: cover;
            background-repeat: repeat;
            background-position: center;
            min-height: 160px;
            padding: 16px;
            font-size: 14px;
            line-height: 1.45;
            color: #111;
            display: flex;
            flex-direction: column;
          }

          .wa-time {
            text-align: center;
            font-size: 12px;
            color: #6b6b6b;
            margin-bottom: 16px;
            font-weight: 400;
            text-shadow: 0 1px 1px rgb(255 255 255 / 60%);
          }

          .wa-typing-bubble {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background: var(--bubble-typing-bg);
            border-radius: 18px;
            border-bottom-left-radius: 4px;
            padding: 10px 14px;
            box-shadow: 0 8px 16px rgba(0,0,0,.12);
            border: 1px solid rgba(0,0,0,.08);
            min-height: 40px;
            max-width: 75%;
            font-size: 16px;
            line-height: 1;
            color: #333;
            opacity: 0;
            transform: translateY(6px) scale(.98);
            transition: all .2s ease;
            align-self: flex-start;
            margin-left: 4px;
            position: relative;
          }

          .wa-typing-bubble::after {
            content: "";
            position: absolute;
            bottom: -1px;
            left: -6px;
            width: 10px;
            height: 10px;
            background: var(--bubble-typing-bg);
            border-left: 1px solid rgba(0,0,0,.08);
            border-bottom: 1px solid rgba(0,0,0,.08);
            border-bottom-left-radius: 4px;
            transform: rotate(45deg);
          }

          .wa-typing-bubble.show {
            opacity: 1;
            transform: translateY(0) scale(1);
          }

          .typing-dots {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-weight: 600;
            color: #333;
            font-size: 20px;
            line-height: 0;
            letter-spacing: 2px;
          }

          .typing-dots span {
            display: inline-block;
            animation: waBounce 1s infinite ease-in-out;
          }
          .typing-dots span:nth-child(2) { animation-delay: .15s; }
          .typing-dots span:nth-child(3) { animation-delay: .3s; }

          .wa-bubble-in {
            background: var(--bubble-bg-user);
            border-radius: 18px;
            border-bottom-left-radius: 4px;
            box-shadow: 0 8px 16px rgba(0,0,0,.12);
            border: 1px solid rgba(0,0,0,.06);
            padding: 12px 14px;
            font-size: 15px;
            line-height: 1.4;
            color: #000;
            max-width: 85%;
            display: inline-block;
            margin-top: 12px;
            opacity: 0;
            transform: translateY(6px) scale(.98);
            transition: all .22s ease;
            align-self: flex-start;
            margin-left: 4px;
            position: relative;
          }

          .wa-bubble-in::after {
            content: "";
            position: absolute;
            bottom: -1px;
            left: -6px;
            width: 10px;
            height: 10px;
            background: var(--bubble-bg-user);
            border-left: 1px solid rgba(0,0,0,.06);
            border-bottom: 1px solid rgba(0,0,0,.06);
            border-bottom-left-radius: 4px;
            transform: rotate(45deg);
          }

          .wa-bubble-in.show {
            opacity: 1;
            transform: translateY(0) scale(1);
          }

          .wa-bubble-out {
            background: #dff6dd;
            border-radius: 18px;
            border-bottom-right-radius: 4px;
            box-shadow: 0 8px 16px rgba(0,0,0,.08);
            border: 1px solid rgba(0,0,0,.04);
            padding: 10px 14px;
            font-size: 15px;
            line-height: 1.4;
            color: #0b3d0b;
            max-width: 78%;
            margin-top: 12px;
            align-self: flex-end;
            margin-left: auto;
            position: relative;
            animation: bubbleIn .22s ease forwards;
          }

          .wa-bubble-out::after {
            content: "";
            position: absolute;
            bottom: -1px;
            right: -6px;
            width: 10px;
            height: 10px;
            background: #dff6dd;
            border-right: 1px solid rgba(0,0,0,.04);
            border-bottom: 1px solid rgba(0,0,0,.04);
            border-bottom-right-radius: 4px;
            transform: rotate(-45deg);
          }

          .wa-bubble-meta {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            font-size: 11px;
            color: #8c9aa5;
            margin-top: 4px;
            gap: 4px;
          }

          .wa-checks {
            width: 14px;
            height: 14px;
            color: #53bdeb;
          }

          .wa-input-row {
            display: flex;
            align-items: center;
            padding: 12px;
            gap: 8px;
            border-top: 1px solid rgba(0,0,0,.07);
            background: #fff;
          }

          .wa-input-field {
            flex-grow: 1;
            font-size: 14px;
            line-height: 1.4;
            padding: 10px 12px;
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,.12);
            color: #000;
            outline: none;
          }
          .wa-input-field::placeholder {
            color: #999;
          }

          .wa-send-btn {
            flex-shrink: 0;
            width: 40px;
            height: 40px;
            border-radius: var(--radius-full);
            border: 0;
            cursor: pointer;
            background: var(--wa-green);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0;
            box-shadow: 0 10px 20px rgba(0,0,0,.18);
            transition: all .15s ease-in-out;
          }
          .wa-send-btn:hover {
            background: var(--wa-green-dark);
            box-shadow: 0 14px 28px rgba(0,0,0,.22);
            transform: scale(1.05);
          }

          .wa-send-icon {
            width: 20px;
            height: 20px;
            color: #fff;
          }

          @media (max-width: 400px) {
            .wa-chat-window {
              width: 100%;
              right: 0;
              left: 0;
              margin: 0 auto;
              bottom: 88px;
            }
          }

          @keyframes waBounce {
            0%, 80%, 100% {
              transform: scale(0.6);
              opacity: 0.3;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }

          @keyframes bubbleIn {
            from { opacity: 0; transform: translateY(6px) scale(.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
        `,
       }}
     />

      <div id="wa-widget-container">
        {/* POPUP */}
        <div className={`wa-chat-window ${open ? "" : "hidden"}`}>
          {/* Botón cerrar */}
          <button
            className="wa-close-absolute"
            aria-label="Cerrar chat"
            onClick={() => setOpen(false)}
          >
            ✕
          </button>

          {/* Header */}
          <div className="wa-header">
            <div className="wa-header-left">
              <div className="wa-avatar-wrapper">
                <div className="wa-avatar">
                  <img
                    src={whatsappLogo}
                    alt="El Ancla CR"
                  />
                </div>
                <div className="wa-online-dot"></div>
              </div>

              <div className="wa-header-texts">
                <div className="wa-brand-name">El Ancla CR</div>
                <div className="wa-status-line">
                  <span className="wa-online-label">En línea</span>
                </div>
              </div>
            </div>
          </div>

          {/* Mensajes */}
          <div className="wa-messages-area">
            <div className="wa-time">{timeStr}</div>

            {typingPhase === 'initial' && (
              <div className="wa-typing-bubble show">
                <div className="typing-dots">
                  <span>•</span>
                  <span>•</span>
                  <span>•</span>
                </div>
              </div>
            )}

            {greetingVisible && (
              <div className="wa-bubble-in show">
                Hola 👋
                <br />
                <br />
                ¿Cómo puedo ayudarte?
              </div>
            )}

            {userMessage && (
              <>
                <div className="wa-bubble-out">
                  {userMessage}
                  <div className="wa-bubble-meta">
                    <span>{timeStr}</span>
                    <svg className="wa-checks" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        fill="currentColor"
                        d="M9.5 15.6l-3-3a1 1 0 0 1 1.4-1.4l1.6 1.58 4.7-4.68a1 1 0 1 1 1.42 1.41l-6.12 6.13a1 1 0 0 1-1.42 0zM13.5 15.6l-3-3a1 1 0 0 1 1.4-1.4l1.6 1.58 4.7-4.68a1 1 0 1 1 1.42 1.41l-6.12 6.13a1 1 0 0 1-1.42 0z"
                      />
                    </svg>
                  </div>
                </div>
                {typingPhase === 'question' && (
                  <div className="wa-typing-bubble show" style={{ alignSelf: 'flex-start' }}>
                    <div className="typing-dots">
                      <span>•</span>
                      <span>•</span>
                      <span>•</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {questionVisible && (
              <div className="wa-bubble-in show" style={{ marginTop: 12 }}>
                ¡Perfecto! ¿Cuál es tu nombre para ayudarte mejor?
              </div>
            )}
          </div>

          {/* Input */}
          <div className="wa-input-row">
            <input
              ref={inputRef}
              className="wa-input-field"
              type="text"
              placeholder={
                messageStep === 1
                  ? "Cuéntanos en qué podemos ayudarte..."
                  : "¿Cuál es tu nombre?"
              }
              onKeyDown={handleKeyDown}
            />

            <button
              className="wa-send-btn"
              aria-label={messageStep === 1 ? "Enviar mensaje" : "Ir a WhatsApp"}
              onClick={handleSend}
            >
              <svg
                className="wa-send-icon"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M2.5 20.5L21.5 12L2.5 3.5L2.5 10L15.5 12L2.5 14L2.5 20.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* BOTÓN FLOTANTE */}
        <button
          type="button"
          className="wa-button"
          aria-label="Abrir chat de WhatsApp"
          onClick={() => setOpen(!open)}
        >
          <svg
            className="wa-icon"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.003 3C8.832 3 3 8.555 3 15.497c0 3.733 1.51 7.07 3.977 9.41L5.5 29l4.29-1.348c1.98.543 3.4.74 5.328.74 7.17 0 13-5.555 13-12.497C28.118 8.555 23.173 3 16.003 3zm7.51 17.605c-.322.91-1.87 1.74-2.58 1.8-.66.06-1.48.09-2.39-.15-.55-.14-1.25-.41-2.15-.8-3.78-1.63-6.24-5.42-6.43-5.67-.19-.25-1.54-2.05-1.54-3.92 0-1.87.97-2.79 1.32-3.18.35-.39.77-.49 1.02-.49.25 0 .51 0 .74.01.24.01.56-.09.88.67.32.76 1.09 2.63 1.19 2.82.1.19.16.41.03.66-.13.25-.2.41-.39.63-.19.22-.41.49-.58.66-.19.19-.39.4-.17.79.22.39.99 1.63 2.14 2.64 1.47 1.3 2.71 1.7 3.1 1.89.39.19.61.16.83-.1.22-.26.96-1.12 1.22-1.5.25-.38.51-.32.85-.19.35.13 2.2 1.04 2.58 1.23.38.19.64.28.74.44.09.16.09.91-.23 1.82z"
              fill="#fff"
            />
          </svg>
        </button>
      </div>
    </>
  );
}
